import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchGamification, upsertGamification, upsertUserActivity, upsertUserSiteTime, upsertUserStudyTime } from "@/lib/db";
import { getRankProgress, TITLE_DEFINITIONS } from "@/lib/titles";
import { useTimeTrackingStore } from "@/stores/useTimeTrackingStore";
import { useGamificationStore, DEFAULT_STATS } from "@/stores/useGamificationStore";
import { triggerRankUp, triggerStreakMilestone, triggerStudyReward } from "@/stores/useNotificationStore";
import { toast } from "sonner";
import i18n from "@/i18n/i18n";

/** Streak days that trigger a "Medal Unlocked" toast (once per milestone). */
const STREAK_MILESTONES = [7, 14, 30];

// ─── Re-export store hook for components ───────────────────────────────────────

export { useGamificationStore };

// ─── GamificationContext: syncs store with Supabase + provides addCrystals with toasts ───

interface GamificationContextType {
  crystals: number;
  xp: number;
  currentStreak: number;
  studyHistory: string[];
  stats: import("@/stores/useGamificationStore").GamificationStats;
  ownedItems: string[];
  equippedItems: string[];
  equippedTheme: string | null;
  equippedCardSkin: string;
  addCrystals: (
    amount: number,
    reason: string,
    options?: { isLearningAction?: boolean; silent?: boolean }
  ) => void | { totalAdd: number; multiplierApplied: boolean };
  spendCrystals: (amount: number) => void;
  addXp: (amount: number) => void;
  addOwnedItem: (itemId: string) => void;
  equipItem: (itemId: string, category?: import("@/stores/useGamificationStore").EquipCategory) => void;
  unequipItem: (itemId: string) => void;
  incrementStat: <K extends keyof import("@/stores/useGamificationStore").GamificationStats>(key: K, amount?: number) => void;
  completeLearningSession: (payload: import("@/stores/useGamificationStore").CompleteLearningSessionPayload) => void;
  finishStudySession: (wordsCount: number, isTest: boolean) => void;
}

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

const SYNC_DEBOUNCE_MS = 1500;

export function GamificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const store = useGamificationStore.getState();
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeSyncInFlightRef = useRef(false);
  const lastSyncedRef = useRef<string>("");

  const crystals = useGamificationStore((s) => s.crystals);
  const xp = useGamificationStore((s) => s.xp);
  const currentStreak = useGamificationStore((s) => s.currentStreak);
  const studyHistory = useGamificationStore((s) => s.studyHistory);
  const stats = useGamificationStore((s) => s.stats);
  const ownedItems = useGamificationStore((s) => s.ownedItems);
  const equippedItems = useGamificationStore((s) => s.equippedItems);
  const equippedTheme = useGamificationStore((s) => s.equippedTheme);
  const equippedCardSkin = useGamificationStore((s) => s.equippedCardSkin);

  // Sync to Supabase when gamification state changes (debounced)
  const scheduleSync = useCallback(() => {
    if (!user) return;

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

    syncTimeoutRef.current = setTimeout(async () => {
      syncTimeoutRef.current = null;
      const state = useGamificationStore.getState();
      const key = JSON.stringify({
        crystals: state.crystals,
        xp: state.xp,
        currentStreak: state.currentStreak,
        lastActivityDate: state.lastActivityDate,
        studyHistory: state.studyHistory,
        stats: state.stats,
        ownedItems: state.ownedItems,
        equippedItems: state.equippedItems,
        equippedTheme: state.equippedTheme,
        equippedCardSkin: state.equippedCardSkin,
      });
      if (key === lastSyncedRef.current) return;
      lastSyncedRef.current = key;

      try {
        await upsertGamification(user.id, {
          crystals: state.crystals,
          xp: state.xp,
          currentStreak: state.currentStreak,
          lastActivityDate: state.lastActivityDate,
          studyHistory: state.studyHistory,
          stats: state.stats,
          ownedItems: state.ownedItems,
          equippedItems: state.equippedItems,
          equippedTheme: state.equippedTheme,
          equippedCardSkin: state.equippedCardSkin,
        });
      } catch (err) {
        console.warn("[GamificationProvider] Sync failed:", err);
      }
    }, SYNC_DEBOUNCE_MS);
  }, [user]);

  // Subscribe to store changes and schedule sync
  useEffect(() => {
    const unsub = useGamificationStore.subscribe(() => scheduleSync());
    return () => {
      unsub();
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [scheduleSync]);

  // Live time tracking: app time + study time. Persisted to localStorage, synced to DB.
  useEffect(() => {
    if (!user?.id) return;
    useTimeTrackingStore.getState().restoreFromStorage();

    const syncToDb = async () => {
      if (timeSyncInFlightRef.current) return;

      const snapshot = useTimeTrackingStore.getState();
      const siteMinutes = Math.max(Math.floor(snapshot.siteSeconds / 60), snapshot.siteSeconds > 0 ? 1 : 0);
      // Short sessions contribute at least 1 minute to stats
      const studyMinutes = Math.max(
        Math.floor(snapshot.studySeconds / 60),
        snapshot.studySeconds > 0 ? 1 : 0
      );
      const deltaSite = Math.max(0, siteMinutes - snapshot.lastSyncedSiteMinutes);
      const deltaStudy = Math.max(0, studyMinutes - snapshot.lastSyncedStudyMinutes);
      if (deltaSite === 0 && deltaStudy === 0) return;

      timeSyncInFlightRef.current = true;
      try {
        const today = new Date().toISOString().slice(0, 10);
        let syncedSite = 0;
        let syncedStudy = 0;

        if (deltaSite > 0) {
          try {
            const didSyncSite = await upsertUserSiteTime(user.id, today, deltaSite);
            if (didSyncSite) syncedSite = deltaSite;
          } catch (err) {
            console.warn("[GamificationContext] upsertUserSiteTime failed:", err);
          }
        }

        if (deltaStudy > 0) {
          try {
            const didSyncStudy = await upsertUserStudyTime(user.id, today, deltaStudy);
            if (didSyncStudy) syncedStudy = deltaStudy;
          } catch (err) {
            console.warn("[GamificationContext] upsertUserStudyTime failed:", err);
          }
        }

        if (syncedSite > 0 || syncedStudy > 0) {
          const current = useTimeTrackingStore.getState();
          useTimeTrackingStore.getState().markSynced(
            current.lastSyncedSiteMinutes + syncedSite,
            current.lastSyncedStudyMinutes + syncedStudy
          );
        }
      } finally {
        timeSyncInFlightRef.current = false;
      }
    };

    const tick = () => {
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;
      useTimeTrackingStore.getState().addSiteSeconds(60);
      const { isInStudyView } = useTimeTrackingStore.getState();
      if (isInStudyView) {
        useTimeTrackingStore.getState().addStudySeconds(60);
      }
    };

    const minInterval = setInterval(tick, 60_000);
    const syncInterval = setInterval(() => {
      void syncToDb();
    }, 5 * 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void syncToDb();
      }
    };
    const onPageHide = () => {
      void syncToDb();
    };
    void syncToDb();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      clearInterval(minInterval);
      clearInterval(syncInterval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      void syncToDb();
    };
  }, [user?.id]);

  // Achievement notifications: send rank/streak medals to the notification center.
  useEffect(() => {
    if (!user?.id) return;
    const state = useGamificationStore.getState();
    const xp = state.xp;
    const currentStreak = state.currentStreak;
    const lastNotifiedRankIndex = state.lastNotifiedRankIndex;
    const lastNotifiedStreakMilestones = state.lastNotifiedStreakMilestones;

    const progress = getRankProgress(xp);
    const rankIndex = TITLE_DEFINITIONS.findIndex((t) => t.id === progress.current.id);
    if (rankIndex >= 0 && rankIndex > lastNotifiedRankIndex && rankIndex > 0) {
      triggerRankUp(progress.current.id, user.id);
      useGamificationStore.getState().setState({ lastNotifiedRankIndex: rankIndex });
    }

    for (const milestone of STREAK_MILESTONES) {
      if (currentStreak >= milestone && !lastNotifiedStreakMilestones.includes(milestone)) {
        triggerStreakMilestone(milestone, user.id);
        useGamificationStore.getState().setState({
          lastNotifiedStreakMilestones: [...lastNotifiedStreakMilestones, milestone],
        });
        break; // one toast per effect run
      }
    }
  }, [currentStreak, user?.id, xp]);

  // Fetch from Supabase on load / user change
  useEffect(() => {
    if (!user) {
      // Anonymous: keep localStorage state, no fetch
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await fetchGamification(user.id);
        if (cancelled) return;

        if (data) {
          useGamificationStore.getState().hydrate({
            crystals: data.crystals,
            xp: data.xp,
            currentStreak: data.currentStreak,
            lastActivityDate: data.lastActivityDate,
            studyHistory: data.studyHistory ?? useGamificationStore.getState().studyHistory,
            stats: data.stats ?? useGamificationStore.getState().stats,
            ownedItems: data.ownedItems,
            equippedItems: data.equippedItems,
            equippedTheme: data.equippedTheme !== undefined ? data.equippedTheme : useGamificationStore.getState().equippedTheme,
            equippedCardSkin: data.equippedCardSkin ?? useGamificationStore.getState().equippedCardSkin,
          });
        } else {
          // fetch returned null (no row / columns missing): force 0 crystals for NEW accounts.
          // Never keep localStorage values when user is logged in.
          useGamificationStore.getState().hydrate({
            crystals: 0,
            xp: 0,
            currentStreak: 0,
            lastActivityDate: null,
            studyHistory: [],
            stats: DEFAULT_STATS,
            ownedItems: [],
            equippedItems: [],
            equippedTheme: null,
            equippedCardSkin: "default",
          });
        }
      } catch (err) {
        if (!cancelled) console.warn("[GamificationProvider] Fetch failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const addCrystals = useCallback(
    (amount: number, reason: string, options?: { isLearningAction?: boolean; silent?: boolean }) => {
      const { totalAdd, multiplierApplied } = useGamificationStore.getState().addCrystals(amount, options);

      if (options?.silent) {
        return { totalAdd, multiplierApplied };
      }

      if (amount > 0) {
        const multiplierSuffix = multiplierApplied ? i18n.t("gamification.multiplierActive") : "";
        toast.success(`+${totalAdd} 💎 ${reason}${multiplierSuffix}`, {
          duration: 2200,
          style: { background: "hsl(var(--card-bg))", border: "1px solid hsl(var(--border))" },
        });
      }
      if (totalAdd > amount && options?.isLearningAction && !multiplierApplied) {
        toast.success(`+${totalAdd - amount} 💎 ${i18n.t("gamification.dailyEntry")}`, {
          duration: 2800,
          style: { background: "hsl(var(--card-bg))", border: "1px solid hsl(var(--border))" },
        });
      }
    },
    []
  );

  const spendCrystals = useCallback((amount: number) => {
    useGamificationStore.getState().spendCrystals(amount);
  }, []);

  const addXp = useCallback((amount: number) => {
    useGamificationStore.getState().addXp(amount);
  }, []);

  const addOwnedItem = useCallback((itemId: string) => {
    useGamificationStore.getState().addOwnedItem(itemId);
  }, []);

  const equipItem = useCallback((itemId: string, category?: import("@/stores/useGamificationStore").EquipCategory) => {
    useGamificationStore.getState().equipItem(itemId, category);
  }, []);

  const unequipItem = useCallback((itemId: string) => {
    useGamificationStore.getState().unequipItem(itemId);
  }, []);

  const incrementStat = useCallback((key: keyof import("@/stores/useGamificationStore").GamificationStats, amount = 1) => {
    useGamificationStore.getState().incrementStat(key, amount);
  }, []);

  const completeLearningSession = useCallback(
    (payload: import("@/stores/useGamificationStore").CompleteLearningSessionPayload) => {
      const result = useGamificationStore.getState().completeLearningSession(payload);
      const crystalsText =
        result != null
          ? `${result.totalAdd} 💎${result.multiplierApplied ? i18n.t("gamification.multiplierActive") : ""}`
          : `${payload.crystalReward} 💎`;
      toast.success(i18n.t("gamification.rewardReceived", { xp: payload.xpReward, crystals: crystalsText }), {
        duration: 2800,
        style: { background: "hsl(var(--card-bg))", border: "1px solid hsl(var(--border))" },
      });
      if (result != null && result.totalAdd > 0) {
        triggerStudyReward(result.totalAdd);
      }
    },
    []
  );

  const finishStudySession = useCallback(
    (wordsCount: number, isTest: boolean) => {
      useGamificationStore.getState().finishStudySession(wordsCount, isTest);
      // Activity count is now recorded per-card in recordCardInteraction to capture
      // partial sessions. No extra upsertUserActivity here to avoid double-counting.
    },
    []
  );

  const value: GamificationContextType = {
    crystals,
    xp,
    currentStreak,
    studyHistory,
    stats,
    ownedItems,
    equippedItems,
    equippedTheme,
    equippedCardSkin,
    addCrystals,
    spendCrystals,
    addXp,
    addOwnedItem,
    equipItem,
    unequipItem,
    incrementStat,
    completeLearningSession,
    finishStudySession,
  };

  return <GamificationContext.Provider value={value}>{children}</GamificationContext.Provider>;
}

export function useGamification(): GamificationContextType {
  const ctx = useContext(GamificationContext);
  if (ctx === undefined) {
    throw new Error("useGamification must be used within GamificationProvider");
  }
  return ctx;
}
